import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { LuArrowLeft, LuArrowRight, LuCheck, LuCircleCheck, LuCircleDashed, LuLock, LuMedal, LuTrophy } from 'react-icons/lu';
import { ContestStateBadge } from './ContestStateBadge';
import { useBoundaryReload } from './useBoundaryReload';
import { Button, ButtonLink } from '../../ui/Button';
import { Countdown } from '../../ui/Countdown';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { DifficultyLabel, Meter } from '../../ui/Labels';
import { cx } from '../../ui/cx';
import { useToast } from '../../ui/Toast';
import { useDocumentTitle, useStrings, useTenant } from '../../tenant/TenantProvider';
import { contestApi } from '../../lib/contestApi';
import { formatDateTime, formatDuration } from '../../lib/format';
import { useNow } from '../../hooks/useNow';

function ActionPanel({ contest, now, busy, onRegister, onLeave }) {
  const t = useStrings();
  const start = Date.parse(contest.startTime);
  const end = Date.parse(contest.endTime);
  const live = contest.state === 'live';
  const upcoming = contest.state === 'upcoming';

  let hint = t('contest.notRegisteredHint');
  if (upcoming && contest.registered) hint = t('contest.registeredHint');
  if (live) hint = contest.registered ? t('contest.liveHint') : t('contest.lateHint');
  if (contest.state === 'ended') hint = t('contest.judgingHint');
  if (contest.state === 'finalized') hint = t('contest.finalHint');
  if (contest.state === 'cancelled') hint = t('contest.cancelledHint');

  const actionable = (upcoming && !contest.registered) || live;

  return (
    <section className={cx('panel contest-action lit', actionable && 'energy energy--glow')}>
      <div className="panel__body">
        {(upcoming || live) && (
          <div className="contest-action__clock">
            <span className="contest-action__label">{t(live ? 'arena.timeLeft' : 'contest.startsInLabel')}</span>
            <Countdown ms={(live ? end : start) - now} size="md" />
            {live && <Meter value={Math.max(0, end - now)} max={end - start} label={t('arena.timeLeft')} />}
          </div>
        )}

        <div className="contest-action__buttons">
          {(upcoming || live) && !contest.registered && (
            <Button variant="primary" size="lg" block loading={busy} onClick={onRegister}>
              {t('contests.register')}
            </Button>
          )}
          {upcoming && contest.registered && (
            <>
              <p className="contest-action__status">
                <LuCircleCheck aria-hidden="true" />
                {t('contests.registered')}
              </p>
              <Button variant="ghost" size="sm" loading={busy} onClick={onLeave}>
                {t('contests.leave')}
              </Button>
            </>
          )}
          {live && contest.registered && (
            <ButtonLink to={`/contests/${contest.id}/arena`} variant="primary" size="lg" block trail={LuArrowRight}>
              {t('contests.enter')}
            </ButtonLink>
          )}
          {(live || contest.state === 'ended') && (
            <ButtonLink to={`/contests/${contest.id}/standings`} block viewTransition>
              {t('contests.standings')}
            </ButtonLink>
          )}
          {contest.state === 'finalized' && (
            <ButtonLink to={`/contests/${contest.id}/standings`} variant="primary" size="lg" block viewTransition>
              {t('contests.results')}
            </ButtonLink>
          )}
        </div>
        <p className="contest-action__hint muted">{hint}</p>
      </div>
      <dl className="contest-facts">
        <div>
          <dt>{t('contest.starts')}</dt>
          <dd>{formatDateTime(contest.startTime)}</dd>
        </div>
        <div>
          <dt>{t('contest.duration')}</dt>
          <dd>{formatDuration(contest.durationMinutes)}</dd>
        </div>
        <div>
          <dt>{t('contest.penalty')}</dt>
          <dd>{t('contest.penaltyValue', { minutes: contest.penaltyMinutes })}</dd>
        </div>
      </dl>
    </section>
  );
}

function ProblemList({ contest, problems }) {
  const t = useStrings();
  const navigate = useNavigate();

  if (!problems) {
    return (
      <div className="locked-problems">
        <LuLock aria-hidden="true" />
        <span>{contest.state === 'upcoming' ? t('contest.problemsLocked') : t('contest.problemsRegisterFirst')}</span>
      </div>
    );
  }

  const hrefFor = (problem) => (contest.state === 'finalized' ? `/problems/${problem.id}` : `/contests/${contest.id}/arena?problem=${problem.id}`);
  const clickable = contest.state === 'finalized' || (contest.state === 'live' && contest.registered);

  return (
    <div className="table-card">
      <table className={`data-table contest-problems${clickable ? ' data-table--interactive' : ''}`}>
        <thead>
          <tr>
            <th>{t('contest.columnProblem')}</th>
            <th>{t('contest.columnDifficulty')}</th>
            <th className="is-numeric">{t('contest.columnPoints')}</th>
            <th className="is-numeric">{t('contest.columnStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {problems.map((problem) => (
            <tr key={problem.id} onClick={clickable ? () => navigate(hrefFor(problem), { viewTransition: true }) : undefined}>
              <td>
                <span className="contest-problem__label mono">{problem.label}</span>
                {clickable ? (
                  <Link to={hrefFor(problem)} onClick={(event) => event.stopPropagation()} className="contest-problem__title" viewTransition>
                    {problem.title}
                  </Link>
                ) : (
                  <span className="contest-problem__title">{problem.title}</span>
                )}
              </td>
              <td>
                <DifficultyLabel level={problem.difficulty} className="difficulty-pill" />
              </td>
              <td className="is-numeric contest-problems__points">{problem.points}</td>
              <td className="is-numeric">
                {problem.solved ? (
                  <span className="contest-problem__status contest-problem__status--solved">
                    <LuCircleCheck aria-hidden="true" />
                    {t('contest.solved')}
                  </span>
                ) : problem.attempted ? (
                  <span className="contest-problem__status contest-problem__status--attempted">
                    <LuCircleDashed aria-hidden="true" />
                    {t('contest.attempted')}
                  </span>
                ) : (
                  <span className="subtle">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ContestPage() {
  const { contestId } = useParams();
  const t = useStrings();
  const { list } = useTenant();
  const { show } = useToast();
  const now = useNow(1000);
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setState((current) => ({ ...current, status: 'loading' }));
      const response = await contestApi.detail(contestId);
      if (response.success) setState({ status: 'ready', data: response.data, error: null });
      else if (!silent) setState({ status: 'error', data: null, error: response.status === 404 ? t('contest.notFound') : response.error });
    },
    [contestId, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const contest = state.data?.contest;
  useDocumentTitle(contest?.name ?? t('contests.title'));

  const boundary = contest
    ? contest.state === 'upcoming'
      ? Date.parse(contest.startTime)
      : contest.state === 'live'
        ? Date.parse(contest.endTime)
        : null
    : null;
  const silentReload = useCallback(() => load({ silent: true }), [load]);
  useBoundaryReload(boundary, now, silentReload);

  const act = async (request, successMessage) => {
    setBusy(true);
    const response = await request(contestId);
    setBusy(false);
    show(response.success ? successMessage : response.error, response.success ? 'success' : 'error');
    silentReload();
  };

  if (state.status === 'loading' && !contest) {
    return (
      <div className="page">
        <Skeleton width={90} />
        <Skeleton width="40%" height={28} style={{ marginTop: 16 }} />
        <Skeleton width="60%" style={{ marginTop: 12 }} />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="page not-found">
        <EmptyState
          icon={LuTrophy}
          title={state.error}
          action={
            <ButtonLink to="/contests" variant="primary">
              {t('contest.back')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  const rules = (list('contest.rules') ?? []).map((rule) => rule.replace('{penalty}', contest.penaltyMinutes));

  return (
    <div className="page contest-page">
      <Link to="/contests" className="back-link" viewTransition>
        <LuArrowLeft aria-hidden="true" />
        {t('contest.back')}
      </Link>
      <header className="page-header contest-page__header">
        <div>
          <div className="contest-page__title-row">
            <h1 className="page-title">{contest.name}</h1>
            <ContestStateBadge state={contest.state} />
          </div>
          <p className="page-subtitle">
            {t('contests.problemCount', { count: contest.problemCount })} · {t('contests.pointCount', { count: contest.totalPoints })} ·{' '}
            {t('contests.participantCount', { count: contest.participants })}
          </p>
        </div>
      </header>

      <div className="page-grid">
        <div className="contest-page__main">
          {contest.description && (
            <div className="contest-page__description">
              <ReactMarkdown>{contest.description}</ReactMarkdown>
            </div>
          )}

          <section className="contest-page__section">
            <h2 className="section-title">{t('contest.problemsTitle')}</h2>
            <ProblemList contest={contest} problems={state.data.problems} />
          </section>

          {contest.state === 'finalized' && contest.winners.length > 0 && (
            <section className="contest-page__section">
              <h2 className="section-title">{t('contest.winnersTitle')}</h2>
              <ol className="winners">
                {contest.winners.map((winner) => (
                  <li key={winner.rank} className={cx('winners__item', winner.rank <= 3 && `winners__item--${winner.rank}`)}>
                    <span className="winners__rank tabular">{winner.rank <= 3 ? <LuMedal aria-label={String(winner.rank)} /> : winner.rank}</span>
                    <span className="winners__name">{winner.username}</span>
                    <span className="winners__score tabular muted">{t('contests.pointCount', { count: winner.score })}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="contest-page__section">
            <h2 className="section-title">{t('contest.rulesTitle')}</h2>
            <ul className="rules">
              {rules.map((rule) => (
                <li key={rule}>
                  <LuCheck aria-hidden="true" />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </section>

          {contest.state === 'cancelled' && <Alert tone="warning">{t('contest.cancelledHint')}</Alert>}
        </div>

        <aside className="page-rail">
          <ActionPanel
            contest={contest}
            now={now}
            busy={busy}
            onRegister={() => act(contestApi.register, t('contests.registeredToast', { name: contest.name }))}
            onLeave={() => act(contestApi.unregister, t('contests.leftToast', { name: contest.name }))}
          />
        </aside>
      </div>
    </div>
  );
}
